// src/contexts/OrganizationContext.js - Version multi-tenant

import React, { createContext, useContext, useState, useEffect } from 'react'
import { get, put } from '../utils/apiClient'
import defaultLogo from '../assets/images/terrilink-logo.png'

const OrganizationContext = createContext()

export const useOrganization = () => {
  const context = useContext(OrganizationContext)
  if (!context) {
    throw new Error('useOrganization must be used within an OrganizationProvider')
  }
  return context
}

const DEFAULT_DESCRIPTION =
  "Un espace privé pour échanger, partager des opportunités, et renforcer les liens entre membres, où qu'ils se trouvent."

const DEFAULT_ORG_NAME = 'Terrilink'

// Paths super-admin purs (système d'auth séparé, AdminLayout dédié).
// MUST stay in sync avec SUPER_ADMIN_PATHS dans App.js. Tous les autres
// /admin/* (ex: /admin/promotions, /admin/mentor-applications) sont des
// routes admin TENANT qui héritent du DefaultLayout et nécessitent la
// config org (layoutTemplate, theme, logo...) hydratée.
const SUPER_ADMIN_PATH_PREFIXES = [
  '/admin/login',
  '/admin/organizations',
  '/admin/webhooks',
  '/admin/system',
  '/admin/deploy-jobs',
]
// Match exact ou sous-chemin (PAS startsWith brut qui matcherait
// /admin/systeme par accident).
const isPathOrChild = (pathname, prefix) =>
  pathname === prefix || pathname.startsWith(`${prefix}/`)
const isSuperAdminPath = (pathname) =>
  pathname === '/admin' ||
  SUPER_ADMIN_PATH_PREFIXES.some((p) => isPathOrChild(pathname, p))

export const OrganizationProvider = ({ children }) => {
  const [config, setConfig] = useState({
    name: DEFAULT_ORG_NAME,
    logoUrl: defaultLogo,
    logoBackgroundColor: '#ffffff',
    theme: 'default',
    loginDescription: DEFAULT_DESCRIPTION,
    // Template de layout admin (desktop only) — 'sidebar' (actuel) ou 'topbar'.
    // Le mobile reste gere par BottomNavigation independamment, cette valeur
    // est ignoree sur viewport mobile cote frontend. Migration 107.
    layoutTemplate: 'sidebar',
    // i18n — langues du tenant (hydratées par loadConfig). Défaut FR seul.
    defaultLocale: 'fr',
    supportedLocales: ['fr'],
  })
  // Paramètres de création d'événements
  const [eventSettings, setEventSettings] = useState({
    membersCanCreateEvents: true,
    membersCanCreatePaidEvents: false,
    stripeConfigured: false,
  })
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  // `document.title` n'est plus posé ici mais par <DocumentTitle />, écrivain
  // unique : il doit y préfixer le nombre de messages non lus, et deux sources
  // sur la même propriété se seraient écrasées (les effets du provider
  // s'exécutant après ceux de ses enfants, c'est ce contexte qui l'emportait et
  // effaçait le compteur). Ici on ne gère plus que les balises Open Graph.
  const updateOpenGraphMeta = (organizationName) => {
    if (organizationName && organizationName.trim()) {
      const name = organizationName.trim()
      const metaTitle = document.querySelector('meta[property="og:title"]')
      if (metaTitle) {
        metaTitle.setAttribute('content', name)
      }
      const metaSiteName = document.querySelector('meta[property="og:site_name"]')
      if (metaSiteName) {
        metaSiteName.setAttribute('content', name)
      }
    }
  }

  const loadConfig = async () => {
    setLoading(true)
    setError(null)

    try {
      const timestamp = Date.now()

      // Appels parallèles via apiClient
      const [logoData, themeData, descriptionData, eventSettingsData, layoutData, localesData] =
        await Promise.all([
          get(`/organization/logo?t=${timestamp}`).catch(() => ({ logoUrl: null })),
          get(`/organization/theme`).catch(() => ({ theme: 'default' })),
          get(`/organization/description?t=${timestamp}`).catch(() => ({
            description: DEFAULT_DESCRIPTION,
          })),
          get('/organization/event-settings').catch(() => ({
            membersCanCreateEvents: true,
            membersCanCreatePaidEvents: false,
            stripeConfigured: false,
          })),
          // Layout template (desktop only) — fail-safe sur 'sidebar' si endpoint
          // pas dispo (ex: backend pre-migration 107).
          get('/organization/layout-template').catch(() => ({ layoutTemplate: 'sidebar' })),
          // i18n — langues du tenant. Fail-safe FR seul si endpoint pas dispo
          // (backend pre-migration 149) → le switcher reste masqué, 0 régression.
          get('/organization/locales').catch(() => ({
            defaultLocale: 'fr',
            supportedLocales: ['fr'],
          })),
        ])

      const newConfig = {
        name: logoData.organizationName || DEFAULT_ORG_NAME,
        logoUrl: logoData.logoUrl ? `${logoData.logoUrl}?t=${timestamp}` : defaultLogo,
        logoBackgroundColor: logoData.logoBackgroundColor || '#ffffff',
        theme: themeData.theme || 'default',
        loginDescription: descriptionData.description || DEFAULT_DESCRIPTION,
        layoutTemplate: layoutData.layoutTemplate === 'topbar' ? 'topbar' : 'sidebar',
        defaultLocale: localesData.defaultLocale || 'fr',
        supportedLocales: Array.isArray(localesData.supportedLocales)
          ? localesData.supportedLocales
          : ['fr'],
      }

      setConfig(newConfig)

      // Mise à jour des paramètres événements
      setEventSettings({
        membersCanCreateEvents: eventSettingsData.membersCanCreateEvents ?? true,
        membersCanCreatePaidEvents: eventSettingsData.membersCanCreatePaidEvents ?? false,
        stripeConfigured: eventSettingsData.stripeConfigured ?? false,
      })

      // Appliquer ET sauvegarder le thème
      document.documentElement.setAttribute('data-theme', newConfig.theme)
      localStorage.setItem('organization_theme', newConfig.theme)

      // Idem pour layoutTemplate — set sur <html> pour que les overrides CSS
      // ([data-layout-template="topbar"] .wrapper) s'appliquent et neutralisent
      // le margin-left/padding-left que CoreUI reserve pour le sidebar fixed.
      document.documentElement.setAttribute('data-layout-template', newConfig.layoutTemplate)
      localStorage.setItem('organization_layout_template', newConfig.layoutTemplate)

      updateOpenGraphMeta(newConfig.name)
    } catch (err) {
      console.error("Erreur lors du chargement de la configuration de l'organisation:", err)
      setError(err.message)
      updateOpenGraphMeta(config.name)

      const savedTheme = localStorage.getItem('organization_theme') || 'default'
      document.documentElement.setAttribute('data-theme', savedTheme)
      const savedLayout =
        localStorage.getItem('organization_layout_template') === 'topbar' ? 'topbar' : 'sidebar'
      document.documentElement.setAttribute('data-layout-template', savedLayout)
    } finally {
      setLoading(false)
    }
  }

  const refreshLogo = async () => {
    const timestamp = Date.now()
    try {
      const data = await get(`/organization/logo?t=${timestamp}`)
      setConfig((prev) => ({
        ...prev,
        logoUrl: data.logoUrl ? `${data.logoUrl}?t=${timestamp}` : defaultLogo,
        logoBackgroundColor: data.logoBackgroundColor || prev.logoBackgroundColor,
      }))
    } catch (err) {
      console.error('Erreur lors du rechargement du logo:', err)
    }
  }

  const updateTheme = async (newTheme) => {
    try {
      await put('/organization/theme', { theme: newTheme })

      setConfig((prev) => ({ ...prev, theme: newTheme }))

      document.documentElement.setAttribute('data-theme', newTheme)
      localStorage.setItem('organization_theme', newTheme)

      return { success: true }
    } catch (err) {
      console.error('Erreur lors de la mise à jour du thème:', err)
      return { success: false, error: err.message }
    }
  }

  const updateLogoBackgroundColor = async (color) => {
    try {
      await put('/organization/logo-background-color', { color })
      setConfig((prev) => ({ ...prev, logoBackgroundColor: color }))
      window.dispatchEvent(new CustomEvent('logoChanged'))
      return { success: true }
    } catch (err) {
      console.error('Erreur MAJ couleur fond logo:', err)
      return { success: false, error: err.message }
    }
  }

  /**
   * Met à jour le template de layout admin (desktop only).
   * @param {'sidebar'|'topbar'} newLayout
   * Backend valide strict via enum, frontend valide aussi pour ne pas envoyer
   * une requete inutile en cas de bug d'appel cote caller.
   */
  const updateLayoutTemplate = async (newLayout) => {
    if (newLayout !== 'sidebar' && newLayout !== 'topbar') {
      return { success: false, error: 'Layout invalide (sidebar|topbar uniquement)' }
    }
    try {
      await put('/organization/layout-template', { layoutTemplate: newLayout })
      // Coercion safe symetrique avec loadConfig (ne fait jamais confiance
      // aveuglement a une string inattendue) + skip setConfig si valeur deja
      // identique pour eviter un re-render inutile de l'arbre.
      const safeLayout = newLayout === 'topbar' ? 'topbar' : 'sidebar'
      setConfig((prev) => {
        if (prev.layoutTemplate === safeLayout) return prev
        return { ...prev, layoutTemplate: safeLayout }
      })
      // Sync attribut HTML + localStorage pour que les overrides CSS
      // [data-layout-template="topbar"] s'appliquent immediatement.
      document.documentElement.setAttribute('data-layout-template', safeLayout)
      localStorage.setItem('organization_layout_template', safeLayout)
      return { success: true }
    } catch (err) {
      console.error('Erreur MAJ layout template:', err)
      return { success: false, error: err.message }
    }
  }

  const updateDescription = async (newDescription) => {
    try {
      await put('/organization/description', { description: newDescription })

      // Mettre à jour immédiatement le state local
      setConfig((prev) => ({ ...prev, loginDescription: newDescription }))

      // Déclencher un événement global pour informer les autres composants
      window.dispatchEvent(
        new CustomEvent('descriptionChanged', {
          detail: { description: newDescription },
        }),
      )

      return { success: true }
    } catch (err) {
      console.error('Erreur lors de la mise à jour de la description:', err)
      return { success: false, error: err.message }
    }
  }

  const updateEventSettings = async (newSettings) => {
    try {
      const response = await put('/organization/event-settings', newSettings)

      // Mettre à jour immédiatement le state local
      setEventSettings((prev) => ({
        ...prev,
        ...newSettings,
        stripeConfigured: response.stripeConfigured ?? prev.stripeConfigured,
      }))

      // Déclencher un événement global pour informer les autres composants
      window.dispatchEvent(
        new CustomEvent('eventSettingsChanged', {
          detail: { ...newSettings },
        }),
      )

      return { success: true }
    } catch (err) {
      console.error('Erreur lors de la mise à jour des paramètres événements:', err)
      return { success: false, error: err.message }
    }
  }

  const refreshEventSettings = async () => {
    try {
      const data = await get('/organization/event-settings')
      setEventSettings({
        membersCanCreateEvents: data.membersCanCreateEvents ?? true,
        membersCanCreatePaidEvents: data.membersCanCreatePaidEvents ?? false,
        stripeConfigured: data.stripeConfigured ?? false,
      })
    } catch (err) {
      console.error('Erreur lors du rechargement des paramètres événements:', err)
    }
  }

  // Premier useEffect : appliquer depuis localStorage au montage
  // Empeche le FOUC (flash of unstyled content) avant le fetch /organization/*.
  useEffect(() => {
    const savedTheme = localStorage.getItem('organization_theme') || 'default'
    document.documentElement.setAttribute('data-theme', savedTheme)
    const savedLayout =
      localStorage.getItem('organization_layout_template') === 'topbar' ? 'topbar' : 'sidebar'
    document.documentElement.setAttribute('data-layout-template', savedLayout)
  }, [])

  useEffect(() => {
    // Hotfix 2026-05-24 : ne skipper QUE les vrais paths super-admin.
    // Avant : /admin/* tout court (trop large) → cassait /admin/mentor-applications
    // (route admin tenant qui hérite du DefaultLayout et a besoin de la config
    // org pour choisir le bon layoutTemplate, sinon fallback 'sidebar').
    if (!isSuperAdminPath(window.location.pathname)) {
      loadConfig()
    } else {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    const handleLogoChange = () => {
      refreshLogo()
    }

    const handleDescriptionChange = (e) => {
      setConfig((prev) => ({
        ...prev,
        loginDescription: e.detail.description,
      }))
    }

    window.addEventListener('logoChanged', handleLogoChange)
    window.addEventListener('descriptionChanged', handleDescriptionChange)

    return () => {
      window.removeEventListener('logoChanged', handleLogoChange)
      window.removeEventListener('descriptionChanged', handleDescriptionChange)
    }
  }, [])

  useEffect(() => {
    updateOpenGraphMeta(config.name)
  }, [config.name])

  const value = {
    config,
    eventSettings,
    loading,
    error,
    loadConfig,
    refreshLogo,
    updateOpenGraphMeta,
    updateTheme,
    updateLogoBackgroundColor,
    updateLayoutTemplate,
    updateDescription,
    updateEventSettings,
    refreshEventSettings,
  }

  return <OrganizationContext.Provider value={value}>{children}</OrganizationContext.Provider>
}
